// src/services/calls.js — the browser call engine.
//
// 🟢 THE WEB SPEAKS THE PHONE'S CALL PROTOCOL, VERBATIM. Same signaling server, same
//    event names, same payload shapes as server/WebRTCService.ts in the app:
//      dial    → 'call-initiate' {fromUserId, toUserId, callType, offer, callId}
//      ring    ← 'incoming-call' {fromUserId, callType, offer, callId, callerData}
//      answer  → 'call-accept'   {callId, answer, deviceId, deviceType:'web'}
//      connect ← 'call-accepted' {answer, callId, acceptedAt}
//      ICE     ⇄ 'ice-candidate' {callId, candidate, targetUserId}
//      end     ⇄ 'call-end' / 'call-ended' / 'call-rejected' / 'call-timeout' / 'call-busy'
//    Media is DTLS-SRTP encrypted by WebRTC itself — same as the phones.
//
// 🟢 MULTI-DEVICE IS FREE: the server rings EVERY live socket of the callee, and
//    cancelRingOnOtherDevices dismisses the phones (CallKit + Android notification)
//    the moment this browser answers — and dismisses this browser the moment a phone
//    answers ('call-ended' reason 'answered_elsewhere').
//
// 🟢 DEVICE SWITCH: 'call-switch-initiate' pulls a live call from another of MY
//    devices onto this browser; 'call-switch-offer' is the other side of the same
//    coin — MY PEER moved their end to another device and this browser re-answers
//    the new offer in place (the call stays up, brief "Reconnecting…").
import { getSocket, BACKEND_URL } from './socket';
import { auth } from '../config/firebase';
import { decide, rebaseOnNetworkReturn, SOCKET_GRACE_MS, RETRY_MIN_MS, ringExpired, RING_DEADLINE_MS, CALLER_GIVE_UP_CAP_MS } from './callResilience';
import { CallQualityLog, startSampling } from './callQualityLog';
import { getIdentity } from '../crypto/identity';
import { getUserProfile } from './users';
import { blockedEitherNow } from './blocking';   // 🚫

/** POST JSON to a call route with this account's ID token in the body. */
async function postWithIdToken(url, body) {
  const idToken = await idTokenNow();
  return fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...body, ...(idToken ? { idToken } : {}) }) });
}
/** This account's ID token, quickly (a slow refresh never holds up a call). */
function idTokenNow(ms = 2500) {
  return Promise.race([
    Promise.resolve().then(() => auth?.currentUser?.getIdToken()).catch(() => null),
    new Promise((r) => setTimeout(() => r(null), ms)),
  ]);
}

const listeners = new Set();
const state = {
  status: 'idle',        // idle | outgoing | incoming | connecting | active
  /* ⭐ set at teardown for a call worth rating, cleared by the card */
  lastEnded: null,
  callId: null,
  peerUid: null,
  callType: 'voice',     // 'voice' | 'video'
  callerData: null,      // profile of the peer (incoming rings carry it)
  muted: false,
  cameraOff: false,
  peerMuted: false,      // the other side muted (shown as a chip)
  peerCameraOff: false,  // the other side turned their camera off
  peerVideoHeld: false,  // 📶 the other side holds its video for a weak network (the apps, r117)
  /* 🟢 WHICH CAMERA WE ARE SENDING. Kept in the state, not only in the module,
     because the self-view mirrors ONLY the front camera — a mirrored rear shot
     puts the text in the world back to front, which reads as a rendering bug. */
  facing: 'user',        // 'user' (front) | 'environment' (rear)
  startedAt: null,       // ms — when the call went active (drives the timer)
  note: '',              // one-line status under the name ("Ringing…", "Connecting…")
  upgradeAsk: false,     // peer asked to turn this voice call into video
  upgradePending: false, // we asked and are waiting
  localStream: null,
  remoteStream: null,
  // 🟢 GREEN BUTTON: a call this ACCOUNT has active on ANOTHER device (phone).
  elsewhere: null,       // { callId, peerUid, callType, platform }
  error: '',
};

function emit() { for (const fn of listeners) { try { fn({ ...state }); } catch {} } }
export function subscribeCalls(fn) { listeners.add(fn); fn({ ...state }); return () => listeners.delete(fn); }
export function getCallState() { return { ...state }; }

/** The rating card calls this once it has read (or dismissed) the last call. */
export function clearLastEndedCall() { state.lastEnded = null; emit(); }

let pc = null;
/* ⭐ CALL QUALITY — the browser polls its own stats (unlike the phone, which
   piggy-backs on the bitrate controller's existing tick). Started when media
   actually connects, stopped at teardown, and its summary latched onto
   `state.lastEnded` so the rating card can read it after the dock has gone. */
let qualityLog = null;
let stopSampling = null;
let myUid = null;
let myDeviceId = null;
let attachedSocket = null;
let pendingRemoteIce = [];      // candidates that arrived before setRemoteDescription
let localIceHistory = [];       // replayed after call-accepted (late-answer insurance)
let iceServers = null;          // cached TURN config
let iceFetchedAt = 0;
let stopSendRamp = null;        // ⭐ ANSWER SPEED — see startSendProfileRamp()
/* 🩺 RECONNECT — see services/callResilience.js. The browser used to hang up the
   moment ICE said `failed`; these hold the recovery instead. */
let downSince = 0;              // when media stopped, 0 while it is up
let everConnected = false;      // a call that never connected is not "reconnecting"
let dropAts = [];               // recent drops, for the flap test
let lastTryAt = 0;              // last ICE restart
let reconnectTimer = null;      // the tick
let firstDropAt = 0;            // 🌐 start of the OUTAGE — the one clock a network return never re-bases
let announcedDown = false;      // is the banner up
let settleTimer = null;         // holding a recovery on a flaky link
let iceRestartBusy = false;     // one negotiation at a time
let sawRoutableCandidate = false; // a srflx/relay candidate has been gathered
let ringAudioGuard = null;      // 75s local failsafe on outgoing
let incomingGuard = null;       // the ring must also expire from the inside — see armRingDeadline
let incomingSince = 0;          // when THIS browser started ringing
let switchPending = null;       // { newCallId } while pulling a call to this browser
let ringbackStyle = 'us';       // 'us' (440+480Hz, 2s/4s) | 'intl' (425Hz, 1s/4s) — set per peer's number
let ringbackCtx = null;
let ringbackTimer = null;

// ── connect chime — the same note the phones play (assets/sounds/call_connected.wav) ──
/* 🔔 The first time a call is really connected, one short, quiet, round note —
   a pitch that settles from 620 to 740 Hz over 60ms and rings down — and for the
   length of it the OTHER person's voice is turned down on this end (the remote
   <audio>/<video> elements' volume; nothing is sent or withheld, nobody's mic is
   touched) so the note is heard. Both ends do the same. Synthesized like the
   ringback: no asset, no fetch, nothing to fail on a cold tab. */
const CONNECT_CHIME_HOLD_MS = 650;
const CONNECT_CHIME_DUCK = 0.3;
let connectChimeTimer = null;
function remoteMediaElements() {
  try {
    return Array.from(document.querySelectorAll('audio,video')).filter((el) => el.srcObject && el.srcObject === state.remoteStream);
  } catch (e) { return []; }
}
function playConnectedChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const t0 = ctx.currentTime + 0.02;
    /* r116 ("very lower, smoother and calmer"): two soft notes a fifth apart — C5, then G5 140ms later —
       30ms in, ~280ms ring-down; the phones render the same (AudioRouteModule renderConnectedCue) */
    const note = (freq, at, amp) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(freq, t0 + at);
      g.gain.setValueAtTime(0, t0 + at);
      g.gain.linearRampToValueAtTime(amp, t0 + at + 0.03);          // 30ms in — no click
      g.gain.setTargetAtTime(0, t0 + at + 0.03, 0.28);              // 280ms ring-down
      o.connect(g).connect(ctx.destination);
      o.start(t0 + at); o.stop(t0 + at + 1.2);
    };
    note(523.25, 0, 0.06); note(783.99, 0.14, 0.048);
    setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1600);
  } catch (e) { /* a chime must never break a call */ }
  try {
    const els = remoteMediaElements();
    els.forEach((el) => { try { el.volume = CONNECT_CHIME_DUCK; } catch (e) {} });
    if (connectChimeTimer) clearTimeout(connectChimeTimer);
    connectChimeTimer = setTimeout(() => {
      connectChimeTimer = null;
      remoteMediaElements().forEach((el) => { try { el.volume = 1; } catch (e) {} });
    }, CONNECT_CHIME_HOLD_MS);
  } catch (e) {}
}

// ── ringback ("peep… peep…") — synthesized, no assets needed ────────────────
// US: dual 440+480 Hz, 2s on / 4s off. International: single 425 Hz, 1s on / 4s off.
// Started only when the callee's DEVICE confirms it is ringing ('call-ringing'),
// exactly like a real phone network — dialing itself is silent.
function startRingback() {
  stopRingback();
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    ringbackCtx = new AC();
    const style = ringbackStyle;
    const cycle = () => {
      if (!ringbackCtx) return;
      try {
        const g = ringbackCtx.createGain();
        g.gain.value = 0.055;
        g.connect(ringbackCtx.destination);
        const freqs = style === 'us' ? [440, 480] : [425];
        const oscs = freqs.map((f) => {
          const o = ringbackCtx.createOscillator();
          o.type = 'sine'; o.frequency.value = f; o.connect(g); o.start();
          return o;
        });
        const onMs = style === 'us' ? 2000 : 1000;
        setTimeout(() => { oscs.forEach((o) => { try { o.stop(); } catch {} }); try { g.disconnect(); } catch {} }, onMs);
        ringbackTimer = setTimeout(cycle, onMs + 4000);
      } catch {}
    };
    cycle();
  } catch {}
}
function stopRingback() {
  if (ringbackTimer) { clearTimeout(ringbackTimer); ringbackTimer = null; }
  try { ringbackCtx?.close(); } catch {}
  ringbackCtx = null;
}

/** Show a terminal outgoing verdict ("Call declined" / "Call not answered") for a
 *  beat before the dock closes — an instant vanish reads as a glitch. */
function lingerEnd(message) {
  stopRingback();
  const cid = state.callId;
  state.note = message;
  emit();
  setTimeout(() => { if (state.callId === cid) teardown(); }, 2500);
}

/* 🟢 EXPORTED FOR THE GROUP ENGINE, which needs the same servers and has no
   reason to run a second cache of them: one fetch, one hour, both callers. */
export async function fetchIce() {
  if (iceServers && Date.now() - iceFetchedAt < 60 * 60 * 1000) return iceServers;
  try {
    /* 🟥 M9: 2.5 s on a guest's first-ever fetch over slow mobile data was often not enough — the call
          went out STUN-only and failed behind a carrier NAT; and a STUN-only answer was cached for an
          hour. 🟢 6 s, this account's token (the server serves TURN freely to it), and only a
          TURN-bearing answer is kept. */
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 6000);
    const tok = await idTokenNow(1500);
    const r = await fetch(`${BACKEND_URL}/api/get-turn-credentials`, { signal: ctl.signal, ...(tok ? { headers: { Authorization: `Bearer ${tok}` } } : {}) });
    clearTimeout(t);
    const j = await r.json();
    const hasTurn = Array.isArray(j?.iceServers) && j.iceServers.some((x) => /turns?:/.test(String(Array.isArray(x?.urls) ? x.urls.join(',') : (x?.urls || x?.url || ''))));
    if (Array.isArray(j?.iceServers) && j.iceServers.length) {
      if (hasTurn) { iceServers = j.iceServers; iceFetchedAt = Date.now(); }
      else return j.iceServers;
    }
  } catch {}
  return iceServers || [{ urls: 'stun:stun.l.google.com:19302' }];
}

/** Wait (bounded) for ICE gathering so the SDP we send carries our candidates
 *  INLINE. This makes web answers survive multi-device trickle misrouting: even if
 *  the server delivers the peer's trickled candidates to the wrong device of this
 *  account, the peer has OUR candidates from the SDP, its connectivity checks reach
 *  us, and we learn the reverse path from those checks (peer-reflexive). */
/* 🟥 WAITING FOR `complete` IS WHY ANSWERING IN THE BROWSER FELT SLOW.
      Gathering is not "complete" until EVERY configured server has answered or
      timed out — five Google STUN servers plus every Cloudflare TURN URL. On a
      weak uplink the slowest of them decides, so this reliably burned the whole
      2.5s budget and the caller did not receive the answer until it expired.
      The person had already tapped Answer; they were watching that 2.5s.

   🟢 But we do not need `complete` — we need ENOUGH. The reason the answer is
      sent non-trickle at all is insurance against this account's OTHER devices
      receiving our trickled candidates (see the comment below): one routable
      candidate in the SDP is what buys that insurance, and the rest keep
      trickling normally afterwards through onicecandidate. So this now resolves
      on the first srflx/relay candidate, and `complete` and the deadline remain
      as the two backstops they always were.

   The practical effect on a good connection is nil (gathering completes in
   ~100ms either way); on a bad one it is the difference between an answer that
   lands in half a second and one that lands in three. */
function waitIceGathering(conn, ms = 2500, { enough = false } = {}) {
  return new Promise((resolve) => {
    if (!conn || conn.iceGatheringState === 'complete') return resolve();
    if (enough && sawRoutableCandidate) return resolve();
    let done = false;
    const finish = () => {
      if (done) return; done = true;
      clearTimeout(timer);
      clearInterval(poll);
      try { conn.removeEventListener('icegatheringstatechange', check); } catch {}
      resolve();
    };
    const check = () => { if (conn.iceGatheringState === 'complete') finish(); };
    const poll = enough ? setInterval(() => { if (sawRoutableCandidate) finish(); }, 60) : null;
    const timer = setTimeout(finish, ms);
    conn.addEventListener('icegatheringstatechange', check);
  });
}

function newPc(servers) {
  sawRoutableCandidate = false;
  /* 🟥 1, NOT 10. iceCandidatePoolSize pre-gathers that many COMPLETE candidate
     sets, and with `max-bundle` the call uses exactly ONE transport — so nine of
     them were gathered and discarded, at the cost of nine extra TURN Allocate
     handshakes and forty-odd STUN bindings fired at once on the weakest link the
     call will ever see. Worse, gathering is not complete until all of them
     finish, so the pool directly lengthened the wait above. */
  const conn = new RTCPeerConnection({ iceServers: servers, iceCandidatePoolSize: 1, bundlePolicy: 'max-bundle' });
  conn.onicecandidate = (e) => {
    if (!e.candidate || !state.callId) return;
    const c = e.candidate.toJSON ? e.candidate.toJSON() : e.candidate;
    /* One candidate that is not a LAN address is all the non-trickle answer
       needs; `candidate` is parsed rather than trusting `type`, which some
       browsers leave undefined on the JSON form. */
    if (/ typ (srflx|relay|prflx)/.test(String(c.candidate || ''))) sawRoutableCandidate = true;
    localIceHistory.push(c);
    try { attachedSocket?.emit('ice-candidate', { callId: state.callId, candidate: c, targetUserId: state.peerUid }); } catch {}
  };
  conn.ontrack = (e) => {
    const s = (e.streams && e.streams[0]) || null;
    if (s) { state.remoteStream = s; emit(); }
  };
  conn.onconnectionstatechange = () => {
    if (conn !== pc) return; // stale-pc guard: a swapped-out connection's events must not touch the live call
    console.log('[calls] pc state:', conn.connectionState, '| ice:', conn.iceConnectionState);
    try { qualityLog?.iceState(Date.now(), String(conn.connectionState || '')); } catch (e) {}
    if (conn.connectionState === 'connected' && (state.status === 'connecting' || state.status === 'outgoing' || state.status === 'active')) {
      if (!state.startedAt) state.startedAt = Date.now();
      if (!everConnected) playConnectedChime();   // 🔔 the first connect only — a reconnect is not a connect
      /* 📶 the server counts a call as answered (and a guest link as spent) only once media flows */
      if (!everConnected) { try { attachedSocket?.emit('call-media-connected', { callId: state.callId }); } catch (e) {} }
      everConnected = true;
      onMediaUp();                       // 🩺 clears any reconnect in flight
      state.status = 'active'; state.note = ''; emit();
      try {
        qualityLog?.connected(Date.now());
        if (!stopSampling && pc && qualityLog) stopSampling = startSampling(pc, qualityLog);
      } catch (e) { /* telemetry must never break a call */ }
    }
    /* 🟥 THIS PAIR OF LINES WAS THE WHOLE BUG. `disconnected` narrated a blip
       instantly, and `failed` HUNG UP — so a guest whose internet moved from
       wifi to cellular lost the call outright. Both now go to the coordinator:
       recovery starts at once, the banner waits, and only 90 seconds without
       media ends anything. See services/callResilience.js. */
    if (conn.connectionState === 'disconnected' || conn.connectionState === 'failed') onMediaDown();
  };
  conn.oniceconnectionstatechange = () => { if (conn === pc) console.log('[calls] iceConnectionState:', conn.iceConnectionState); };
  return conn;
}

async function getMedia(video) {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: true,
    video: video ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } } : false,
  });
  state.localStream = stream;
  if (state.muted) stream.getAudioTracks().forEach((t) => { t.enabled = false; });
  emit();
  return stream;
}

/* ══ 🎬 THE SEND PROFILE — why video used to take seconds to appear ═════════
   🟥 THE BROWSER SENT 1280x720@30 FROM THE FIRST FRAME, UNCAPPED. getMedia asks
      for 720p30 and nothing then told the encoder otherwise, so the first thing
      it emitted on any connection — including a phone on two bars — was a full
      720p keyframe, 40-80 KB. At a real cellular uplink of ~300 kbps that is
      over a second FOR ONE FRAME, and the far end shows black until it arrives
      complete. Chrome's own bandwidth estimator does correct this, but only
      after it has measured the link, which takes a few seconds of sending. Those
      seconds are precisely the "the call takes ages once they answer" window.

   🟢 So the browser now does what the phone does: start at 500 kbps and 320x180
      — a ~5 KB keyframe, one round trip, picture present the moment ICE
      connects — and climb as the link proves itself.

   🟢 THE SIGNAL IS `availableOutgoingBitrate`, not our own guesswork. That is
      Chrome's GCC estimate for the nominated candidate pair: the same number the
      browser uses internally, already smoothed, already congestion-aware. The
      phone has to derive its estimate from receiver reports because
      react-native-webrtc does not expose one; here it is simply available, so
      re-implementing the app's controller in the browser would be inventing a
      worse copy of a number we can just read.

   🟥 NO ESTIMATE THIS TICK MEANS HOLD, NEVER CLIMB. The same rule, for the same
      reason, as the phone's null loss sample: an absent measurement is not a
      measurement of zero congestion. */
const WEB_SEND_START_BPS = 500_000;
const WEB_SEND_MAX_BPS = 2_000_000;

function scaleForBps(bps, current) {
  const T360 = 600_000, T720 = 1_200_000;
  const target = bps < T360 ? 4 : bps < T720 ? 2 : 1;
  if (target >= current) return target;                  // going smaller: immediately
  const boundary = current === 4 ? T360 : T720;          // going bigger: 15% headroom,
  return bps >= boundary * 1.15 ? target : current;      // because each change is a keyframe
}

async function applySendProfile(conn, bps, scale) {
  const sender = conn?.getSenders?.().find((s) => s.track && s.track.kind === 'video');
  if (!sender) return false;
  const fps = scale >= 4 ? 20 : scale >= 2 ? 24 : 30;
  const p = sender.getParameters();
  if (!p.encodings || !p.encodings.length) p.encodings = [{}];
  p.encodings[0].maxBitrate = bps;
  p.encodings[0].scaleResolutionDownBy = scale;
  p.encodings[0].maxFramerate = fps;
  p.degradationPreference = 'balanced';
  try {
    await sender.setParameters(p);
  } catch (e) {
    // Some browsers reject degradationPreference on the parameters object; the
    // BITRATE is the part that must not be lost, so retry without it.
    delete p.degradationPreference;
    try { await sender.setParameters(p); } catch (e2) { return false; }
  }
  return true;
}

/* ═══ 🩺 THE RECONNECT COORDINATOR ═══════════════════════════════════════════
   🟥 `failed` DOES NOT MEAN THE CALL IS OVER. It means the path being used is
      gone — which is precisely what a phone moving from wifi to cellular, a lift
      door, or a router reboot looks like. The path can be rebuilt. The phone has
      rebuilt it for a long time; the browser hung up.
   🟢 Recovery starts at once; the banner waits ANNOUNCE_DOWN_MS, because a blip
      that heals itself should never be narrated.                              */
function clearReconnect() {
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
  downSince = 0; lastTryAt = 0; announcedDown = false; iceRestartBusy = false;
  firstDropAt = 0;
}

async function doIceRestart() {
  if (!pc || iceRestartBusy || !state.callId || !state.peerUid) return;
  iceRestartBusy = true;
  lastTryAt = Date.now();
  try {
    const offer = await pc.createOffer({ iceRestart: true });
    await pc.setLocalDescription(offer);
    /* 🟢 the signalling server already relays this for the phone — the browser
       simply never spoke the protocol. Same events, same shape. */
    attachedSocket?.emit('ice-restart', {
      callId: state.callId, targetUserId: state.peerUid, offer: pc.localDescription,
    });
    console.log('[calls] ICE restart sent');
  } catch (e) {
    console.warn('[calls] ICE restart failed:', e?.message);
  } finally { iceRestartBusy = false; }
}

function reconnectTick() {
  reconnectTimer = null;
  if (!pc || !state.callId) return;
  const d = decide({
    state: pc.connectionState, downSince, now: Date.now(),
    everConnected, dropAts, lastTryAt, firstDropAt,
  });
  if (d.action === 'end') {
    console.log('[calls] reconnect deadline —', d.reason);
    endCall('failed');
    return;
  }
  if (d.announce && !announcedDown) {
    announcedDown = true; state.note = 'Reconnecting…'; emit();
  }
  if (d.action === 'restart') doIceRestart();
  if (d.action === 'none') return;
  reconnectTimer = setTimeout(reconnectTick, 1000);
}

function onMediaDown() {
  if (downSince) return;                 // already coordinating
  downSince = Date.now();
  if (!firstDropAt) firstDropAt = downSince;
  dropAts = [...dropAts.filter((t) => Date.now() - t < 60000), downSince].slice(-8);
  if (!reconnectTimer) reconnectTick();
}

/* ═══ 🌐 THE NETWORK CAME BACK ════════════════════════════════════════════════
   🟥 THE BROWSER HAD NO VIEW OF ITS OWN CONNECTION. Recovery was driven entirely
      by `pc.connectionState`, which cannot know that an interface has come back
      — a candidate belonging to an address the machine no longer has never
      re-probes into life. And past LOOP_CAP_MS the loop deliberately stands down
      to WAIT for the network, without anything to tell it the wait is over. So a
      laptop whose wifi returned at second seventy sat out the remaining twenty
      and ended the call, with a working connection the whole time.

   🟢 `online` is the browser's version of the phone's NetInfo return, and it
      lands in the same policy: re-base the window (bounded by the cap that
      nothing re-bases), then restart at once rather than at the next tick. The
      phone does this in WebRTCService.onNetworkReturned. */
function onNetworkReturned() {
  if (!pc || !state.callId) return;
  const st = pc.connectionState;
  if (st === 'closed') return;
  if (st === 'connected' || st === 'completed') { onMediaUp(); return; }
  const r = rebaseOnNetworkReturn({ downSince, firstDropAt, now: Date.now() });
  if (r.end) { console.log('[calls] network back, but past the absolute cap — ending'); endCall('failed'); return; }
  downSince = r.downSince || Date.now();
  if (!firstDropAt) firstDropAt = downSince;
  lastTryAt = 0;                      // the backoff is about failed attempts, not this one
  console.log('[calls] network restored — restarting ICE now');
  doIceRestart();
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(reconnectTick, RETRY_MIN_MS);
}

function onMediaUp() {
  if (!downSince) return;
  const d = decide({ state: 'connected', dropAts, now: Date.now() });
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  downSince = 0; lastTryAt = 0; iceRestartBusy = false;
  firstDropAt = 0;                 // 🟢 the outage is over — the cap starts fresh next time
  const show = () => { announcedDown = false; state.note = ''; state.status = 'active'; emit(); };
  /* 🟥 ON A FLAKY LINK A RECOVERY IS HELD BEFORE IT IS BELIEVED, or the banner
     blinks on and off every few seconds — worse to watch than one that stays. */
  if (d.hold && announcedDown) {
    if (settleTimer) return;
    settleTimer = setTimeout(() => {
      settleTimer = null;
      if (pc && (pc.connectionState === 'connected' || pc.connectionState === 'completed')) show();
    }, d.hold);
    return;
  }
  show();
}

function startSendProfileRamp(conn) {
  let scale = 4;
  let bps = WEB_SEND_START_BPS;
  let applied = false;

  const push = async () => {
    const ok = await applySendProfile(conn, bps, scale);
    if (ok) console.log('[calls] send profile →', Math.round(bps / 1000), 'kbps @',
      `${Math.round(1280 / scale)}x${Math.round(720 / scale)}`);
    return ok;
  };
  push().then((ok) => { applied = ok; });

  const tick = async () => {
    if (conn !== pc || conn.connectionState === 'closed') return;
    /* A video sender can appear mid-call (voice call upgraded to video), and it
       must get the small startup profile too — otherwise the upgrade is the same
       720p stall this whole function exists to remove. */
    if (!applied) { applied = await push(); if (!applied) return; }
    if (conn.connectionState !== 'connected') return;   // unstable → hold

    let est = null;
    try {
      const rep = await conn.getStats();
      rep.forEach((r) => {
        if (r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded'
            && typeof r.availableOutgoingBitrate === 'number') est = r.availableOutgoingBitrate;
      });
    } catch (e) { return; }
    if (!est) return;                                    // no opinion → hold

    const want = Math.max(300_000, Math.min(Math.round(est * 0.85), WEB_SEND_MAX_BPS));
    const nextScale = scaleForBps(want, scale);
    // Dead band: a change costs a keyframe, so ignore drift under 8%.
    if (nextScale === scale && Math.abs(want - bps) < bps * 0.08) return;
    bps = want; scale = nextScale;
    await push();
  };

  const iv = setInterval(tick, 2000);
  return () => clearInterval(iv);
}

function stopMedia() {
  try { state.localStream?.getTracks().forEach((t) => t.stop()); } catch {}
  state.localStream = null;
  state.remoteStream = null;
}

function teardown() {
  /* 🧾 M3: HOW IT ENDED, latched like `lastEnded` — teardown wipes the note ("Busy", "Call declined")
        that said so, and the guest's end screen read nothing and promised the link was spent. */
  if (state.callId) {
    const _n = state.note;
    state.lastVerdict = everConnected ? 'answered'
      : _n === 'Busy' ? 'busy' : _n === 'Call declined' ? 'declined' : _n === 'Call not answered' ? 'no_answer' : 'not_connected';
  }
  /* 🩺 THE RECONNECT LOOP MUST NOT OUTLIVE THE CALL IT BELONGS TO. A timer left
     running would restart ICE on the NEXT call's connection, or on none at all,
     and `everConnected` carried over would make a fresh call that has not
     connected yet announce itself as "Reconnecting…". */
  clearReconnect();
  everConnected = false;
  dropAts = [];
  /* ⭐ CLOSE AND LATCH BEFORE ANYTHING IS CLEARED. The block below empties
     `state` — callId, peerUid, startedAt, callType all go — and the rating card
     needs every one of them. Reading them after the Object.assign would give a
     card about a call with no id. */
  try {
    if (stopSampling) { stopSampling(); stopSampling = null; }
    if (stopSendRamp) { stopSendRamp(); stopSendRamp = null; }
    if (qualityLog && state.callId && state.startedAt) {
      qualityLog.end(Date.now());
      const summary = qualityLog.summary();
      /* 🟢 Only a call that genuinely connected and lasted is worth asking
         about — the same 15s floor the server applies, checked here too so a
         short call does not even cost a round trip. */
      if (summary.durationSec >= 15 && summary.connectMs > 0) {
        state.lastEnded = {
          callId: state.callId,
          peerUid: state.peerUid,
          kind: state.callType === 'video' ? 'video' : 'voice',
          durationSec: summary.durationSec,
          metrics: summary,
          bundle: qualityLog.bundle(),
        };
      }
    }
  } catch (e) { /* never let telemetry block a hang-up */ }
  qualityLog = null;

  try { pc?.close(); } catch {}
  pc = null;
  stopMedia();
  stopRingback();
  pendingRemoteIce = [];
  localIceHistory = [];
  switchPending = null;
  if (ringAudioGuard) { clearTimeout(ringAudioGuard); ringAudioGuard = null; }
  disarmRingDeadline();
  Object.assign(state, {
    status: 'idle', callId: null, peerUid: null, callType: 'voice', callerData: null,
    muted: false, cameraOff: false, peerMuted: false, peerCameraOff: false, peerVideoHeld: false, facing: 'user',
    startedAt: null, note: '', upgradeAsk: false, upgradePending: false,
    /* 🟥 `lastEnded` is NOT reset here — it is set just above and must survive
       this Object.assign, which is the whole point of latching it. The UI clears
       it with clearLastEndedCall() once the card is done with it. */
  });
  emit();
}

async function drainRemoteIce() {
  if (!pc || !pc.remoteDescription) return;
  const q = pendingRemoteIce; pendingRemoteIce = [];
  for (const c of q) { try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch {} }
}

// ── outgoing ────────────────────────────────────────────────────────────────
export async function startCall(peerUid, isVideo, callerProfile) {
  if (state.status !== 'idle') { state.error = 'busy'; emit(); return false; }
  /* 🚫 NOT ACROSS A BLOCK, either direction. The buttons ask first (ChatWindow,
     CallsPanel); this is the door every path shares, and the server refuses
     the dial as well. */
  if (blockedEitherNow(peerUid)) { state.error = 'blocked'; emit(); return false; }
  const s = attachedSocket;
  if (!s || !s.connected) { state.error = 'offline'; emit(); return false; }
  try {
    state.status = 'outgoing'; state.peerUid = peerUid; state.error = '';
    try { qualityLog = new CallQualityLog(); qualityLog.start(Date.now(), isVideo ? 'video' : 'voice'); } catch (e) {}
    state.callType = isVideo ? 'video' : 'voice';
    state.callerData = callerProfile || null;
    state.note = 'Calling…';
    emit();
    // pick the ringback the app would play: +1 numbers get the US cadence,
    // everything else the international one (default US when unknown)
    ringbackStyle = 'us';
    /* 🔒 H2: a guest never reads the owner's profile for their phone number — the ringback is not
          worth handing a stranger someone's number */
    if (!auth?.currentUser?.isAnonymous) {
      getUserProfile(peerUid).then((p) => {
        const num = String(p?.phone || p?.phoneNumber || '').replace(/[^\d+]/g, '');
        if (num && !num.startsWith('+1') && !/^1\d{10}$/.test(num)) ringbackStyle = 'intl';
      }).catch(() => {});
    }
    const [servers] = await Promise.all([fetchIce(), getMedia(isVideo)]);
    if (state.status !== 'outgoing') {
      /* 🟥 M5: hung up while we set up — but getMedia had already opened the camera and mic AFTER
            teardown ran, and nothing stopped them: the camera light stayed on until the tab closed. */
      stopMedia();
      return false;
    }
    pc = newPc(servers);
    state.localStream.getTracks().forEach((t) => pc.addTrack(t, state.localStream));
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    // ⭐ Small first — see startSendProfileRamp. The caller starts encoding the
    //    moment the callee answers, so this must be in place before that.
    if (stopSendRamp) { stopSendRamp(); }
    stopSendRamp = startSendProfileRamp(pc);
    state.callId = `${myUid}-${peerUid}-${Date.now()}`;
    s.emit('call-initiate', {
      fromUserId: myUid, toUserId: peerUid,
      callType: state.callType, offer: pc.localDescription, callId: state.callId,
    });
    emit();
    // local failsafe — the server's call-timeout normally lands first (60 s after
    // the ring-ack, like WhatsApp); this only catches a server that never answers at
    // all, so it sits past the server's hard cap (the same table as the phone)
    ringAudioGuard = setTimeout(() => {
      if (state.status !== 'outgoing') return;
      // 'no_answer': recorded as NOT ANSWERED for both people, never as a cancel
      try { attachedSocket?.emit('call-end', { callId: state.callId, userId: myUid, targetUserId: state.peerUid, timestamp: Date.now(), reason: 'no_answer' }); } catch {}
      lingerEnd('Call not answered');
    }, CALLER_GIVE_UP_CAP_MS);
    return true;
  } catch (e) {
    console.warn('[calls] startCall failed:', e?.message);
    state.error = e?.name === 'NotAllowedError' ? 'mic_denied' : 'failed';
    teardown();
    return false;
  }
}

// ── incoming ────────────────────────────────────────────────────────────────
/* ── the ring has to be able to end itself ──────────────────────────────────
   🟥 THE RING WAS FANNED OUT AND THE STOP WAS NOT. `incoming-call` reaches every
      device on purpose; the server's no-answer timeout, the caller's cancel and a
      decline on the phone each spoke to one socket. The phone went quiet, this
      tab did not, and nothing was ever coming to stop it.

   🟢 THE SERVER FIX IS THE FIX — THIS IS THE PART THAT DOES NOT DEPEND ON DELIVERY.
      Even fanned out, "it is over" is one packet on one socket: a tab that is
      mid-reconnect at that instant hears nothing, and no one repeats it. So the
      ring also expires on facts this tab already holds — the moment it started.

   🟢 AND IT EXPIRES INTO THE SAME PLACE A REAL STOP LANDS. teardown(), not a
      special "gave up" state, so the panel closes and the ringtone stops exactly
      as they would have. A backstop that leaves different wreckage than the
      thing it stands in for is a second bug waiting for its own report. */
function disarmRingDeadline() {
  if (incomingGuard) { clearTimeout(incomingGuard); incomingGuard = null; }
  incomingSince = 0;
}

function armRingDeadline(callId) {
  disarmRingDeadline();
  incomingSince = Date.now();
  const cid = callId;
  incomingGuard = setTimeout(() => {
    incomingGuard = null;
    /* the call moved on while we were waiting — not ours to end */
    if (state.callId !== cid || state.status !== 'incoming') return;
    if (!ringExpired(incomingSince)) return;
    console.warn('[calls] ring deadline — no stop arrived for', cid, '→ dismissing locally');
    teardown();
  }, RING_DEADLINE_MS);
}

async function onIncoming(data) {
  const { fromUserId, callType, offer, callId, callerData } = data || {};
  if (!callId || !offer?.sdp) return;
  if (state.callId === callId) return;                       // redelivery
  if (state.status !== 'idle') return;                       // busy here — phone can still take it
  Object.assign(state, {
    status: 'incoming', callId, peerUid: fromUserId,
    callType: String(callType) === 'video' ? 'video' : 'voice',
    callerData: callerData || null, note: 'Incoming call…',
  });
  state._offer = offer;
  armRingDeadline(callId);
  emit();
  // ring-ack so the caller flips "Calling…" → "Ringing" and the server extends the window
  try {
    fetch(`${BACKEND_URL}/api/call-ringing`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ callId }),
    }).catch(() => {});
  } catch {}
}

export async function acceptCall() {
  if (state.status !== 'incoming' || !state._offer) return;
  const s = attachedSocket;
  const offer = state._offer;
  try {
    state.status = 'connecting'; state.note = 'Connecting…'; emit();
    /* ⭐ the callee's log starts here — the caller's starts in startCall(). Both
       sides measure their OWN leg, which is the only leg either can see. */
    try { qualityLog = new CallQualityLog(); qualityLog.start(Date.now(), state.callType === 'video' ? 'video' : 'voice'); } catch (e) {}
    try {
      // 🟢 name the device, so the server can silence the OTHER ones without
      //    silencing this one — with this account's token, or it silences none.
      postWithIdToken(`${BACKEND_URL}/api/call-answering`, { callId: state.callId, deviceId: myDeviceId || undefined }).catch(() => {});
    } catch {}
    const [servers] = await Promise.all([fetchIce(), getMedia(state.callType === 'video')]);
    pc = newPc(servers);
    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    await drainRemoteIce();
    state.localStream.getTracks().forEach((t) => pc.addTrack(t, state.localStream));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    // ⭐ Small first, before a single frame is encoded — see startSendProfileRamp.
    if (stopSendRamp) { stopSendRamp(); }
    stopSendRamp = startSendProfileRamp(pc);
    /* 🟢 NON-TRICKLE ANSWER: carry our candidates inline (see waitIceGathering) —
       this is what makes answering ON THE WEB robust when this account has other
       devices the server might route trickled candidates to.

       ⭐ `enough` + 1200ms, not `complete` + 2500ms. Every millisecond spent here
       is a millisecond after the person tapped Answer during which the CALLER
       still sees "ringing" — this is the single largest piece of the browser's
       answer latency. One routable candidate is all the insurance above needs;
       the rest trickle as usual. */
    await waitIceGathering(pc, 1200, { enough: true });
    s?.emit('call-accept', {
      callId: state.callId, answer: pc.localDescription,
      deviceId: myDeviceId, deviceType: 'web',   // → the app bubble's "answered from the web" note
    });
    /* 🟢 THE SAME SECOND ROAD THE PHONE TAKES. On a browser the socket is nearly
       always up before anyone can press Answer, so this is insurance rather than
       the rescue it is on a cold phone — but a tab that answers during a
       reconnect has exactly the phone's problem, and the emit above is silently
       dropped. POST /api/call-answer is idempotent by SDP, so whichever arrives
       first connects the call and the other is a no-op. */
    try {
      /* 🔐 the server takes an answer only from the callee's verified identity */
      postWithIdToken(`${BACKEND_URL}/api/call-answer`, {
        callId: state.callId,
        answer: { type: pc.localDescription?.type, sdp: pc.localDescription?.sdp },
        deviceId: myDeviceId || undefined,
      }).catch(() => {});
    } catch {}
    emit();
  } catch (e) {
    console.warn('[calls] accept failed:', e?.message);
    try { attachedSocket?.emit('call-client-error', { callId: state.callId, stage: 'web-accept', error: String(e?.message || e) }); } catch {}
    state.error = e?.name === 'NotAllowedError' ? 'mic_denied' : 'failed';
    endCall('failed');
  }
}

export function rejectCall() {
  if (state.status !== 'incoming') return;
  try { attachedSocket?.emit('call-reject', { callId: state.callId, deviceId: myDeviceId }); } catch {}
  teardown();
}

export function endCall(reason) {
  if (state.status === 'idle') return;
  const s = attachedSocket;
  if (state.callId && state.status !== 'incoming') {
    const payload = { callId: state.callId, userId: myUid, targetUserId: state.peerUid, timestamp: Date.now() };
    /* ⏱️ the length this tab's call timer showed — the server records THIS for every
       device, so the bubble, the chat list and the phone all say the same thing */
    if (state.startedAt) payload.durationSec = Math.max(0, Math.floor((Date.now() - state.startedAt) / 1000));
    try { s?.emit('call-end', payload); } catch {}
    setTimeout(() => { try { s?.emit('call-end', payload); } catch {} }, 500); // same redundancy the app uses
  } else if (state.callId) {
    try { s?.emit('call-reject', { callId: state.callId, deviceId: myDeviceId }); } catch {}
  }
  teardown();
  if (reason === 'failed' || reason === 'no_answer' || reason === 'busy') { state.error = reason; emit(); }
}

// ── in-call controls ────────────────────────────────────────────────────────
export function toggleMute() {
  state.muted = !state.muted;
  try { state.localStream?.getAudioTracks().forEach((t) => { t.enabled = !state.muted; }); } catch {}
  try { attachedSocket?.emit('peer-mute-change', { callId: state.callId, toUserId: state.peerUid, muted: state.muted }); } catch {}
  emit();
}

export function toggleCamera() {
  if (state.callType !== 'video') return;
  state.cameraOff = !state.cameraOff;
  try { state.localStream?.getVideoTracks().forEach((t) => { t.enabled = !state.cameraOff; }); } catch {}
  // 🟢 tell the peer — same event the apps exchange, so their screen shows
  //    "<name>'s camera is off" exactly like an app→app call
  try {
    attachedSocket?.emit('camera-state-change', {
      fromUserId: myUid, toUserId: state.peerUid, callId: state.callId,
      cameraOn: !state.cameraOff, timestamp: Date.now(),
    });
  } catch {}
  emit();
}

/**
 * ⭐ FLIP TO THE OTHER CAMERA.
 *
 * 🟥 replaceTrack ON THE SENDER, NOT A NEW addTrack. Adding a second video track
 *    changes the set of m-lines, which means renegotiation — a fresh
 *    offer/answer round trip in the middle of a live call, for what the person
 *    experiences as a button that swaps a picture. replaceTrack swaps the source
 *    behind an existing sender with no SDP at all, so the far side's receiver
 *    never notices anything but new frames.
 *
 * 🟥 THE OLD TRACK IS STOPPED, AND THAT IS NOT TIDINESS. On a phone the two
 *    cameras are usually one hardware pipeline: leaving the front camera running
 *    is what makes the rear one fail to open, so this reads as "the flip button
 *    does nothing" rather than as a leak.
 *
 * 🟢 Returns false rather than throwing when there is no second camera. A
 *    laptop with one webcam answers OverconstrainedError here, and a call is not
 *    the moment to surface an exception.
 */
export async function flipCamera() {
  if (state.callType !== 'video' || !state.localStream) return false;
  const next = state.facing === 'user' ? 'environment' : 'user';

  let cam = null;
  try {
    cam = await navigator.mediaDevices.getUserMedia({
      audio: false,
      /* `ideal`, not `exact`: a device with one camera should give us that one
         camera back rather than refusing, so the worst case is a no-op flip
         instead of a dead button that also drops the video. */
      video: { facingMode: { ideal: next }, width: { ideal: 1280 }, height: { ideal: 720 } },
    });
  } catch { return false; }

  const track = cam.getVideoTracks()[0];
  if (!track) { try { cam.getTracks().forEach((t) => t.stop()); } catch {} return false; }

  /* 🟥 CARRY THE MUTE ACROSS. A fresh track arrives enabled; flipping the camera
     while it is switched off would put the person back on screen without them
     touching the camera button. */
  track.enabled = !state.cameraOff;

  const sender = pc ? pc.getSenders().find((x) => x.track && x.track.kind === 'video') : null;
  if (sender) {
    try { await sender.replaceTrack(track); }
    catch { try { track.stop(); } catch {} return false; }
  }

  const old = state.localStream.getVideoTracks()[0];
  if (old) {
    try { state.localStream.removeTrack(old); } catch {}
    try { old.stop(); } catch {}
  }
  try { state.localStream.addTrack(track); } catch {}

  state.facing = next;
  emit();
  return true;
}

/**
 * Is there a second camera to flip to?
 *
 * 🟢 ASKED, NOT ASSUMED. A phone has two and a laptop usually has one, and a
 *    flip button on a laptop is a control that visibly does nothing. Device
 *    labels need permission, but the COUNT does not — and during a call we hold
 *    the permission anyway.
 */
export async function hasMultipleCameras() {
  try {
    const list = await navigator.mediaDevices.enumerateDevices();
    return list.filter((d) => d.kind === 'videoinput').length > 1;
  } catch { return false; }
}

// ── voice → video upgrade (the app's exact three-step handshake) ────────────
/* 🟥 M5: a declined (or unanswered) upgrade only DISABLED the track — the camera stayed captured, and on
      a voice call there was no camera button to turn it off. 🟢 The track is stopped and taken out. */
function releaseCamera() {
  try {
    const vids = state.localStream ? state.localStream.getVideoTracks() : [];
    for (const t of vids) {
      try { t.stop(); } catch (e) {}
      try { state.localStream.removeTrack(t); } catch (e) {}
      try { const snd = pc && pc.getSenders().find((x) => x.track === t); if (snd) pc.removeTrack(snd); } catch (e) {}
    }
  } catch (e) {}
  state.cameraOff = false;
}
let upgradeTimer = null;
async function enableCamera() {
  if (!state.localStream) return;
  if (state.localStream.getVideoTracks().length === 0) {
    const cam = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 } } });
    const track = cam.getVideoTracks()[0];
    state.localStream.addTrack(track);
    if (pc) pc.addTrack(track, state.localStream);
  } else {
    state.localStream.getVideoTracks().forEach((t) => { t.enabled = true; });
  }
  state.cameraOff = false;
  emit();
}

export async function requestVideoUpgrade() {
  if (state.status !== 'active' || state.callType === 'video') return;
  try {
    await enableCamera();
    state.upgradePending = true; emit();
    attachedSocket?.emit('video-upgrade-request', {
      fromUserId: myUid, toUserId: state.peerUid, callId: state.callId,
      shouldInitiateRenegotiation: true, timestamp: Date.now(),
    });
    /* 🟥 H7/M5: an upgrade request that reached the wrong device waited for ever ("Waiting for them to
          turn their camera on…"), camera captured. 🟢 20 s, then it is withdrawn. */
    const _cid = state.callId;
    if (upgradeTimer) clearTimeout(upgradeTimer);
    upgradeTimer = setTimeout(() => {
      upgradeTimer = null;
      if (state.callId !== _cid || !state.upgradePending || state.callType === 'video') return;
      state.upgradePending = false;
      releaseCamera();
      try { attachedSocket?.emit('video-upgrade-canceled', { fromUserId: myUid, toUserId: state.peerUid, callId: _cid }); } catch (e) {}
      emit();
    }, 20000);
  } catch (e) { console.warn('[calls] upgrade request failed:', e?.message); }
}

export async function acceptVideoUpgrade() {
  state.upgradeAsk = false; emit();
  try {
    await enableCamera();
    attachedSocket?.emit('video-upgrade-accepted', {
      fromUserId: myUid, toUserId: state.peerUid, callId: state.callId,
      shouldInitiateRenegotiation: true,
    });
  } catch (e) { console.warn('[calls] upgrade accept failed:', e?.message); }
}

export function declineVideoUpgrade() {
  state.upgradeAsk = false; emit();
  try { attachedSocket?.emit('video-upgrade-declined', { fromUserId: myUid, toUserId: state.peerUid, callId: state.callId }); } catch {}
}

// ── device switch: pull a phone call onto this browser ──────────────────────
export async function pullCallHere() {
  const remote = state.elsewhere;
  if (!remote || state.status !== 'idle') return false;
  const s = attachedSocket;
  if (!s || !s.connected) return false;
  try {
    const isVideo = remote.callType === 'video';
    Object.assign(state, {
      status: 'connecting', peerUid: remote.peerUid, callType: remote.callType,
      note: 'Moving the call here…',
    });
    emit();
    const [servers] = await Promise.all([fetchIce(), getMedia(isVideo)]);
    pc = newPc(servers);
    state.localStream.getTracks().forEach((t) => pc.addTrack(t, state.localStream));
    const offer = await pc.createOffer();
    const newCallId = `${myUid}-${remote.peerUid}-${Date.now()}`;
    state.callId = newCallId;              // set BEFORE setLocal so trickle isn't dropped
    await pc.setLocalDescription(offer);
    // 🟢 NON-TRICKLE PULL OFFER: pre-answer trickle to the peer can misroute when
    //    they have several devices — inline candidates make the switch land anyway.
    //    One routable candidate is enough insurance; waiting for `complete` here
    //    just lengthens the "Reconnecting…" the person is watching.
    await waitIceGathering(pc, 1500, { enough: true });
    if (stopSendRamp) { stopSendRamp(); }
    stopSendRamp = startSendProfileRamp(pc);
    switchPending = { newCallId };
    state.elsewhere = null;  // 🟢 STALE-GREEN FIX: the old call is being superseded — never leave its green behind
    console.log(`[calls] SWITCH: pulling ${remote.callId} here as ${newCallId} (peer ${remote.peerUid})`);
    s.emit('call-switch-initiate', {
      oldCallId: remote.callId, newCallId, offer: pc.localDescription,
      fromUserId: myUid, toUserId: remote.peerUid, callType: remote.callType,
      deviceId: myDeviceId, deviceType: 'web',
    });
    emit();
    setTimeout(() => { if (switchPending?.newCallId === newCallId && state.status === 'connecting') endCall('failed'); }, 15000);
    return true;
  } catch (e) {
    console.warn('[calls] pull failed:', e?.message);
    teardown();
    return false;
  }
}

// ── the peer moved THEIR end to another device: re-answer in place ──────────
async function onSwitchOffer(data) {
  const { oldCallId, newCallId, offer, callType } = data || {};
  if (!offer?.sdp || state.callId !== oldCallId || !state.localStream) return;
  try {
    state.note = 'Reconnecting…'; emit();
    const servers = await fetchIce();
    const old = pc;
    pendingRemoteIce = []; localIceHistory = [];
    pc = newPc(servers);
    state.callId = newCallId;
    if (callType) state.callType = String(callType) === 'video' ? 'video' : 'voice';
    state.localStream.getTracks().forEach((t) => pc.addTrack(t, state.localStream));
    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await waitIceGathering(pc, 1500, { enough: true });   // non-trickle re-answer — same misrouting armor
    if (stopSendRamp) { stopSendRamp(); }
    stopSendRamp = startSendProfileRamp(pc);
    attachedSocket?.emit('call-accept', { callId: newCallId, answer: pc.localDescription, deviceId: myDeviceId, deviceType: 'web' });
    try { old?.close(); } catch {}
    emit();
  } catch (e) {
    console.warn('[calls] switch re-answer failed:', e?.message);
    try { attachedSocket?.emit('call-client-error', { callId: newCallId, stage: 'web-switch-reanswer', error: String(e?.message || e) }); } catch {}
  }
}

// ── socket wiring ───────────────────────────────────────────────────────────
export async function attachCalls(uid) {
  myUid = uid;
  // 🔎 build fingerprint — if this line is missing from the console, the browser is
  //    running an OLD bundle and none of the call fixes below are actually live.
  console.log('[calls] engine v3 (non-trickle answers, ring fan-out aware, sync-fix)');
  try { myDeviceId = (await getIdentity())?.deviceId || null; } catch {}
  // the socket comes up a beat after login (identity → handshake) — wait for it
  let s = getSocket();
  for (let i = 0; !s && i < 40; i++) {
    await new Promise((r) => setTimeout(r, 250));
    s = getSocket();
  }
  if (!s || s === attachedSocket) return;
  attachedSocket = s;

  s.on('incoming-call', onIncoming);

  s.on('call-accepted', async ({ answer, callId, acceptedAt }) => {
    if (!pc || callId !== state.callId) return;
    try {
      if (pc.signalingState === 'have-local-offer') {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        await drainRemoteIce();
      }
      switchPending = null;
      /* 🟥 H5: "accepted" was shown as "active" — a running timer — before a single packet flowed. A
            guest behind a NAT that TURN could not cross watched "0:14" in silence, for ever. 🟢 Until
            the peer connection is up it says "Connecting…", the timer starts on THIS clock when media
            does (onconnectionstatechange), and 30 s without media ends the call as failed. */
      if (pc.connectionState === 'connected' || everConnected) {
        state.status = 'active';
        state.startedAt = state.startedAt || Date.now();
        state.note = '';
      } else {
        state.status = 'connecting';
        state.note = 'Connecting…';
        const _cid = callId;
        setTimeout(() => {
          if (state.callId === _cid && !everConnected && state.status === 'connecting') {
            console.warn('[calls] no media 30 s after the answer — ending as failed');
            endCall('failed');
          }
        }, 30000);
      }
      void acceptedAt;
      stopRingback();
      if (ringAudioGuard) { clearTimeout(ringAudioGuard); ringAudioGuard = null; }
      emit();
      // late-answer insurance, same as the app: replay our full candidate history
      const history = [...localIceHistory];
      setTimeout(() => {
        for (const c of history) {
          try { attachedSocket?.emit('ice-candidate', { callId: state.callId, candidate: c, targetUserId: state.peerUid }); } catch {}
        }
      }, 250);
    } catch (e) { console.warn('[calls] apply answer failed:', e?.message); }
  });

  s.on('call-answering', ({ callId }) => {
    if (callId === state.callId && state.status === 'outgoing') { stopRingback(); state.note = 'Connecting…'; emit(); }
  });

  // 🟢 RINGING LADDER: the server confirms the callee's DEVICE actually presented
  //    the call (CallKit shown / notification up / socket ring-ack) — flip
  //    "Calling…" → "Ringing…" and start the ringback tone, like the app.
  s.on('call-ringing', ({ callId }) => {
    if (callId === state.callId && state.status === 'outgoing') {
      state.note = 'Ringing…';
      startRingback();
      emit();
    }
  });

  // 🟢 PEER MUTE — same event the apps exchange; shown as a chip on the dock.
  s.on('peer-mute-change', (d) => {
    if (!d || (d.callId && d.callId !== state.callId)) return;
    state.peerMuted = !!d.muted;
    emit();
  });

  // 🟢 PEER CAMERA — same event the apps exchange.
  s.on('camera-state-change', (d) => {
    if (!d || (d.callId && d.callId !== state.callId)) return;
    state.peerCameraOff = d.cameraOn === false;
    emit();
  });

  /* 📶 r117 — a phone on a weak link holds its video (the camera stays on) and says so: their face, not a frozen frame */
  s.on('call-video-hold', (d) => {
    if (!d || (d.callId && d.callId !== state.callId) || typeof d.held !== 'boolean') return;
    state.peerVideoHeld = d.held;
    emit();
  });

  s.on('ice-candidate', async (d) => {
    const c = d?.candidate;
    if (!c) return;
    if (pc && pc.remoteDescription) { try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch {} }
    else pendingRemoteIce.push(c);
  });

  const remoteEnd = (reason) => (d) => {
    const id = d?.callId;
    if (!id || id !== state.callId) return;
    // handled on another of MY devices (answer/decline/hangup there) → silent dismiss.
    // Covers: incoming ring answered on the phone, declined on the phone, and the
    // call being moved to the phone — the web ring/panel just goes away.
    if (d?.reason === 'answered_elsewhere' || d?.reason === 'declined_elsewhere') { teardown(); return; }
    if (d?.reason === 'switched_device') { teardown(); return; }  // we moved it to the phone
    // 🟢 OUTGOING VERDICTS: show what happened for a beat instead of vanishing.
    if (reason === 'declined' && state.status === 'outgoing') { lingerEnd('Call declined'); return; }
    if (reason === 'no_answer' && state.status === 'outgoing') { lingerEnd('Call not answered'); return; }
    if (reason === 'busy' && state.status === 'outgoing') { lingerEnd('Busy'); return; }
    teardown();
    /* the server's own reason counts too (a link the server refused: link_used, link_expired…) */
    const _why = reason || (d && typeof d.reason === 'string' ? d.reason : '');
    if (_why) { state.error = _why; emit(); }
  };
  s.on('call-end', remoteEnd(''));
  s.on('call-ended', remoteEnd(''));
  s.on('call-rejected', remoteEnd('declined'));
  s.on('call-timeout', remoteEnd('no_answer'));
  s.on('call-busy', remoteEnd('busy'));

  // voice → video upgrade
  s.on('video-upgrade-request', (d) => {
    if (d?.callId !== state.callId) return;
    state.upgradeAsk = true; emit();
  });
  s.on('video-upgrade-accepted', async (d) => {
    if (d?.callId !== state.callId || !pc) return;
    if (upgradeTimer) { clearTimeout(upgradeTimer); upgradeTimer = null; }
    try {
      await enableCamera();
      state.upgradePending = false; state.callType = 'video'; emit();
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      attachedSocket?.emit('webrtc-renegotiation', { callId: state.callId, offer: pc.localDescription, targetUserId: state.peerUid, fromUserId: myUid });
    } catch (e) { console.warn('[calls] upgrade renegotiation failed:', e?.message); }
  });
  s.on('video-upgrade-declined', (d) => {
    if (d?.callId && d.callId !== state.callId) return;
    state.upgradePending = false;
    if (upgradeTimer) { clearTimeout(upgradeTimer); upgradeTimer = null; }
    if (state.callType !== 'video') releaseCamera();
    emit();
  });
  s.on('video-upgrade-canceled', () => { state.upgradeAsk = false; emit(); });

  s.on('webrtc-renegotiation', async (d) => {
    if (!pc || d?.callId !== state.callId || !d?.offer?.sdp) return;
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(d.offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      /* 🟥 THIS WAS 'call-accept' — the CALLEE's answer event. The server read it as such: bounced the
            answer back to us, re-bound the call's callee leg to this tab, and the owner's upgrade hung
            and their hang-up was ignored. 🟢 A renegotiation answer, to the other side — as the phone. */
      attachedSocket?.emit('webrtc-renegotiation-answer', { callId: state.callId, answer: pc.localDescription, targetUserId: state.peerUid, fromUserId: myUid });
      state.callType = 'video'; emit();
    } catch (e) { console.warn('[calls] renegotiation answer failed:', e?.message); }
  });
  s.on('webrtc-renegotiation-answer', async (d) => {
    if (!pc || !d?.answer?.sdp) return;
    try { await pc.setRemoteDescription(new RTCSessionDescription(d.answer)); state.callType = 'video'; emit(); } catch {}
  });

  // 🟢 GREEN BUTTON: this account's call state on OTHER devices
  s.on('active-call-sync', (d) => {
    if (!d?.callId) return;
    const amCaller = String(d.fromUserId) === String(myUid);
    const amCallee = String(d.toUserId) === String(myUid);
    if (!amCaller && !amCallee) return;
    if (d.status === 'active') {
      // "us" checks: our own live call, or a sync whose MY-side leg is THIS device.
      // 🟢 CALLEE FIX: while we are merely RINGING ('incoming'), the call is NOT ours
      //    yet — a sync for that same callId means ANOTHER device answered it, and
      //    this button must go green. Only a call we are actually ON is "us".
      const onIt = state.status === 'active' || state.status === 'connecting' || state.status === 'outgoing';
      if (onIt && String(d.callId) === String(state.callId)) return;
      const myLegDevice = amCaller ? String(d.callerDeviceId || '') : String(d.calleeDeviceId || '');
      if (myDeviceId && myLegDevice && myLegDevice === String(myDeviceId)) return;
      // another device answered the call we were ringing on — drop our stale ring UI
      if (!onIt && String(d.callId) === String(state.callId) && state.status === 'incoming') {
        teardown();
      }
      const peerUid = amCaller ? d.toUserId : d.fromUserId;
      state.elsewhere = { callId: d.callId, peerUid, callType: d.callType === 'video' ? 'video' : 'voice', platform: 'phone' };
    } else if (state.elsewhere?.callId === d.callId) {
      state.elsewhere = null;
    }
    emit();
  });

  /* 🩺 THE OTHER HALF OF THE RESTART. The signalling server has relayed these
     for the phone all along; the browser answered neither, so a restart the
     PHONE initiated died in silence and the guest simply watched the call
     freeze. Perfect-negotiation is not needed here: whoever noticed the break
     restarts, and a colliding restart merely produces one extra renegotiation. */
  s.on('ice-restart-offer', async (d) => {
    if (!pc || !d?.offer || (d.callId && state.callId && d.callId !== state.callId)) return;
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(d.offer));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      s.emit('ice-restart-answer', {
        callId: state.callId, targetUserId: state.peerUid, answer: pc.localDescription,
      });
      console.log('[calls] answered peer ICE restart');
    } catch (e) { console.warn('[calls] ice-restart-offer failed:', e?.message); }
  });
  s.on('ice-restart-answer', async (d) => {
    if (!pc || !d?.answer) return;
    try {
      /* 🟥 ONLY WHEN WE ARE THE ONE WAITING FOR IT. Applying an answer while the
         connection is stable throws and, worse, can tear down a healthy path. */
      if (pc.signalingState !== 'have-local-offer') return;
      await pc.setRemoteDescription(new RTCSessionDescription(d.answer));
      console.log('[calls] peer answered our ICE restart');
    } catch (e) { console.warn('[calls] ice-restart-answer failed:', e?.message); }
  });

  /* 🟥 AND THE SOCKET COMING BACK IS NOT THE SAME AS MEDIA COMING BACK. A guest
     who changes network gets a fresh socket while ICE is still dead; without
     this the call sits there looking connected to the server and silent to the
     person. The grace exists because ICE very often heals itself first, and a
     restart on top of a recovery costs a keyframe for nothing. */
  s.on('connect', () => {
    if (!state.callId || !pc) return;
    setTimeout(() => {
      if (!pc || !state.callId) return;
      const st = pc.connectionState;
      if (st === 'connected' || st === 'completed') return;
      console.log('[calls] socket returned, media still down — restarting ICE');
      onMediaDown();
      doIceRestart();
    }, SOCKET_GRACE_MS);
  });

  /* 🌐 THE BROWSER'S OWN VIEW OF ITS CONNECTION, which is earlier and more
     certain than the socket's reconnect (socket.io has its own backoff to work
     through first) and than `pc.connectionState` (which cannot re-probe a dead
     address at all). Attached once with the socket's handlers, and harmless when
     no call is up — onNetworkReturned's first line is the guard. */
  if (typeof window !== 'undefined' && !window.__alloOnlineHooked) {
    window.__alloOnlineHooked = true;
    window.addEventListener('online', () => {
      console.log('[calls] browser reports online');
      onNetworkReturned();
    });
  }

  s.on('call-switch-offer', onSwitchOffer);
  s.on('call-switch-failed', (d) => {
    console.warn('[calls] SWITCH FAILED:', d?.reason || 'unknown', '| old:', d?.oldCallId);
    if (switchPending && d?.oldCallId) { teardown(); state.error = 'failed'; emit(); }
  });
}
